import { fetchApiJson } from '@vend/utilities'
import * as Ably from 'ably'
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useWebRegisterStatusManager } from '../../ng-compat/hooks'

export type RealtimeContextState = {
  client: Ably.Realtime | null
  serialNumber: string | null
}

export const RealtimeContext = createContext<RealtimeContextState>({
  client: null,
  serialNumber: null,
})

interface RealtimeProviderProps {
  outletId: string
  registerId: string
  children: ReactNode
}

const getTokenUrl = (outletId: string, registerId: string) =>
  `/api/2.0/outlets/${outletId}/lspay/token/${registerId}?pairingData=true`

const getAuthUrl = (registerId: string) =>
  `/api/1.0/cfd/lspay/auth/${registerId}`

export function RealtimeProvider({
  outletId,
  registerId,
  children,
}: RealtimeProviderProps) {
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const isOnline = webRegisterStatusManager?.isOnline() ?? false

  const [client, setClient] = useState<Ably.Realtime | null>(null)
  const [serialNumber, setSerialNumber] = useState<string | null>(null)

  useEffect(() => {
    if (!isOnline || !registerId || !outletId) {
      setClient(null)
      setSerialNumber(null)
      return
    }

    let ablyClient: Ably.Realtime | null = null
    let cancelled = false

    const setupAblyClient = async () => {
      const terminalInfo = await fetchApiJson(getTokenUrl(outletId, registerId))

      if (cancelled) {
        return
      }

      setSerialNumber(terminalInfo.serial_number)

      const newClient = new Ably.Realtime({
        authUrl: getAuthUrl(registerId),
      })

      if (cancelled) {
        newClient.close()
        return
      }

      ablyClient = newClient
      setClient(ablyClient)
    }

    setupAblyClient()

    return () => {
      cancelled = true
      ablyClient?.close()
    }
  }, [isOnline, outletId, registerId])

  return (
    <RealtimeContext.Provider value={{ client, serialNumber }}>
      {children}
    </RealtimeContext.Provider>
  )
}

export function useRealtimeProvider(): RealtimeContextState {
  return useContext(RealtimeContext)
}
